import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import {
  Upload, Loader2, CheckCircle, AlertCircle,
  ChevronDown, MapPin, Clock, Briefcase, DollarSign,
  User, Mail, Link as LinkIcon, FileText, X, ShieldCheck,
  ChevronLeft, Check,
} from "lucide-react";

function formatSalary(role) {
  if (!role.salary_min && !role.salary_max) return null;
  const period = role.salary_period === "hourly" ? "/hr" : "/yr";
  const fmt = (n) => n >= 1000 ? `${(n / 1000).toFixed(0)}k` : String(n);
  if (role.salary_min && role.salary_max) return `${fmt(role.salary_min)} – ${fmt(role.salary_max)}${period}`;
  if (role.salary_min) return `from ${fmt(role.salary_min)}${period}`;
  return `up to ${fmt(role.salary_max)}${period}`;
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const SUBMIT_STEPS = [
  "Parsing your resume…",
  "Matching skills to role…",
  "Saving your application…",
];

function FormInput({ id, label, required, icon: Icon, children, hint }) {
  return (
    <div>
      <label htmlFor={id} className="flex items-center gap-1 font-mono text-[10px] tracking-[0.15em] text-muted-foreground mb-1.5">
        {label}
        {required && <span className="text-[#E11D48] ml-0.5">*</span>}
      </label>
      <div className="relative">
        {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />}
        {children}
      </div>
      {hint && <p className="font-mono text-[9px] text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

export default function ApplyPage() {
  const { roleId } = useParams();
  const [role, setRole] = useState(null);
  const [roleError, setRoleError] = useState(null);
  const [jdOpen, setJdOpen] = useState(false);

  // Consent config (fetched from server)
  const [consentInfo, setConsentInfo] = useState(null); // null = loading, {consent_enabled} = loaded

  // Step: 1 = application form, 2 = consent form
  const [step, setStep] = useState(1);

  const [form, setForm] = useState({ name: "", email: "", linkedin_url: "" });
  const [parsing, setParsing] = useState(false);
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);

  // Consent form state
  const [workAuth, setWorkAuth] = useState("");
  const [noticePeriod, setNoticePeriod] = useState("");
  const [earliestStart, setEarliestStart] = useState("");
  const [consentChecked, setConsentChecked] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [stepIdx, setStepIdx] = useState(-1);
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);

  const fileRef = useRef();
  const submitTimers = useRef([]);
  const parseGenRef = useRef(0);

  useEffect(() => {
    return () => { submitTimers.current.forEach(clearTimeout); };
  }, []);

  useEffect(() => {
    Promise.allSettled([
      axios.get(`${API}/public/apply/${roleId}`),
      axios.get(`${API}/public/apply/${roleId}/consent`),
    ]).then(([roleResult, consentResult]) => {
      if (roleResult.status === "rejected") {
        setRoleError(roleResult.reason?.response?.data?.detail || "Role not found");
        return;
      }
      setRole(roleResult.value.data);
      // Consent fetch failing gracefully — treat as no consent step required
      setConsentInfo(
        consentResult.status === "fulfilled"
          ? consentResult.value.data
          : { consent_enabled: false }
      );
    });
  }, [roleId]);

  const totalSteps = consentInfo?.consent_enabled ? 2 : 1;

  const parseResume = async (f) => {
    const gen = ++parseGenRef.current;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("resume_file", f);
      const { data } = await axios.post(`${API}/public/apply/parse-resume`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (gen !== parseGenRef.current) return;
      const rawLinkedin = data.linkedin_url || "";
      const linkedin = rawLinkedin && !rawLinkedin.startsWith("http")
        ? `https://${rawLinkedin}`
        : rawLinkedin;
      setForm((prev) => ({
        name: prev.name || data.name || "",
        email: prev.email || data.email || "",
        linkedin_url: prev.linkedin_url || linkedin || "",
      }));
    } catch {
      // silent — candidate fills manually
    } finally {
      if (gen === parseGenRef.current) setParsing(false);
    }
  };

  const handleFile = (f) => {
    if (!f) return;
    const ok = ["application/pdf", "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"].includes(f.type)
      || f.name.match(/\.(pdf|doc|docx)$/i);
    if (!ok) { setError("Please upload a PDF or Word document."); return; }
    if (f.size > 10 * 1024 * 1024) { setError("File is too large. Please upload a file under 10 MB."); return; }
    setError(null);
    setFile(f);
    parseResume(f);
  };

  const clearFile = (e) => {
    e.stopPropagation();
    setFile(null);
    setForm({ name: "", email: "", linkedin_url: "" }); // clear auto-filled fields on file remove
    parseGenRef.current++;
    setParsing(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const startSubmitSteps = () => {
    submitTimers.current.forEach(clearTimeout);
    submitTimers.current = [];
    setStepIdx(0);
    submitTimers.current.push(setTimeout(() => setStepIdx(1), 3000));
    submitTimers.current.push(setTimeout(() => setStepIdx(2), 7000));
  };

  const stopSubmitSteps = () => {
    submitTimers.current.forEach(clearTimeout);
    submitTimers.current = [];
    setStepIdx(-1);
  };

  // Step 1 → Step 2 (when consent enabled)
  const handleStep1Next = (e) => {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) return setError("Full name is required.");
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) return setError("Valid email is required.");
    if (!file) return setError("Please upload your resume.");
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Final validation
    if (!form.name.trim()) return setError("Full name is required.");
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) return setError("Valid email is required.");
    if (!file) return setError("Please upload your resume.");

    if (consentInfo?.consent_enabled) {
      if (!consentChecked) return setError("You must agree to the consent form to continue.");
      if (consentInfo.require_work_authorization && !workAuth) return setError("Please select your work authorization status.");
    }

    setSubmitting(true);
    startSubmitSteps();

    const fd = new FormData();
    fd.append("name", form.name.trim());
    fd.append("email", form.email.trim());
    if (form.linkedin_url.trim()) fd.append("linkedin_url", form.linkedin_url.trim());
    fd.append("resume_file", file);

    if (consentInfo?.consent_enabled) {
      fd.append("consent_given", "true");
      if (workAuth) fd.append("work_authorization", workAuth);
      if (noticePeriod) fd.append("notice_period_days", noticePeriod);
      if (earliestStart) fd.append("earliest_start_date", earliestStart);
    }

    try {
      const { data } = await axios.post(`${API}/public/apply/${roleId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      stopSubmitSteps();
      setDone(data);
    } catch (err) {
      stopSubmitSteps();
      if (err?.response?.status === 409) {
        setError("You've already applied for this role with this email.");
      } else {
        setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Error state ──────────────────────────────────────────────────────────────
  if (roleError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#FAFAFA]">
        <div className="text-center max-w-sm">
          <div className="w-14 h-14 bg-[#E11D48]/10 flex items-center justify-center mx-auto mb-5">
            <AlertCircle className="w-7 h-7 text-[#E11D48]" />
          </div>
          <h1 className="font-display font-black text-xl mb-2">Role unavailable</h1>
          <p className="text-sm text-muted-foreground">{roleError}</p>
        </div>
      </div>
    );
  }

  // ── Loading state ────────────────────────────────────────────────────────────
  if (!role || !consentInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#0025D8]" />
      </div>
    );
  }

  // ── Success state ────────────────────────────────────────────────────────────
  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#FAFAFA]">
        <div className="bg-white rounded-xl border border-border max-w-md w-full overflow-hidden">
          <div className="h-1.5 bg-[#10B981]" />
          <div className="p-10 text-center">
            <div className="w-16 h-16 bg-[#10B981]/10 flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-8 h-8 text-[#10B981]" />
            </div>
            <div className="font-mono text-[10px] tracking-[0.25em] text-[#10B981] mb-3">APPLICATION RECEIVED</div>
            <h1 className="font-display font-black text-2xl tracking-tight mb-1">{done.role_title}</h1>
            <p className="font-mono text-xs text-muted-foreground mb-8">at {done.company_name}</p>

            <div className="rounded-xl border border-border p-4 text-left mb-6 space-y-3">
              <div className="font-mono text-[9px] tracking-[0.2em] text-muted-foreground">WHAT HAPPENS NEXT</div>
              {[
                "Your resume has been matched against the role requirements",
                "The hiring team will review your application",
                "If shortlisted, you'll receive an interview invitation by email",
              ].map((s, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="font-mono text-[10px] text-[#0025D8] font-bold flex-shrink-0 mt-0.5">{i + 1}.</span>
                  <p className="text-xs text-muted-foreground">{s}</p>
                </div>
              ))}
            </div>

            <p className="text-sm text-muted-foreground">
              Good luck, <span className="font-semibold text-foreground">{done.candidate_name}</span>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const salary = formatSalary(role);

  // Render consent template with candidate name substituted (or placeholder)
  const renderedConsent = consentInfo?.template_body
    ? consentInfo.template_body
        .replace(/\[Full Name\]/g, form.name.trim() || "[Your Name]")
        .replace(/\[Company Name\]/g, role.company_name || "[Company]")
    : "";
  // Note: [privacy@companyemail.com] is left as-is — companies must replace it with their real address

  // ── Main apply page ──────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-border">
        <div className="h-1 bg-[#0025D8]" />
        <div className="max-w-lg mx-auto px-6 py-7">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 bg-[#0025D8] flex items-center justify-center flex-shrink-0">
              <span className="text-white font-mono font-black text-[8px]">
                {role.company_name?.charAt(0)?.toUpperCase()}
              </span>
            </div>
            <span className="font-mono text-[11px] font-semibold tracking-[0.15em] text-[#0025D8]">
              {role.company_name?.toUpperCase()}
            </span>
          </div>

          <h1 className="font-display font-black text-3xl tracking-tight mb-4 leading-tight">
            {role.title}
          </h1>

          <div className="flex items-center gap-1.5 flex-wrap mb-4">
            <span className="font-mono text-[9px] px-2 py-1 bg-[#10B981] text-white font-bold tracking-[0.1em]">
              OPEN
            </span>
            {role.employment_type && (
              <span className="font-mono text-[10px] px-2 py-1 rounded-xl border border-border text-muted-foreground flex items-center gap-1">
                <Clock className="w-2.5 h-2.5" />{role.employment_type}
              </span>
            )}
            {role.work_arrangement && (
              <span className="font-mono text-[10px] px-2 py-1 rounded-xl border border-border text-muted-foreground flex items-center gap-1">
                <Briefcase className="w-2.5 h-2.5" />{role.work_arrangement}
              </span>
            )}
            {role.location && (
              <span className="font-mono text-[10px] px-2 py-1 rounded-xl border border-border text-muted-foreground flex items-center gap-1">
                <MapPin className="w-2.5 h-2.5" />{role.location}
              </span>
            )}
            {salary && (
              <span className="font-mono text-[10px] px-2 py-1 rounded-md border border-[#10B981]/40 text-[#10B981] bg-[#10B981]/10 flex items-center gap-1 font-semibold">
                <DollarSign className="w-2.5 h-2.5" />{salary}
              </span>
            )}
          </div>

          {role.description && (
            <div className="rounded-xl border border-border">
              <button
                type="button"
                onClick={() => setJdOpen((o) => !o)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-secondary/30 transition-colors"
              >
                <span className="font-mono text-[10px] tracking-[0.15em] text-foreground font-semibold">
                  VIEW JOB DESCRIPTION
                </span>
                <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${jdOpen ? "rotate-180" : ""}`} />
              </button>
              {jdOpen && (
                <div className="px-4 pb-5 pt-1 border-t border-border bg-secondary/10">
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">{role.description}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Form area ──────────────────────────────────────────────────────── */}
      <div className="flex-1 flex items-start justify-center p-6 pt-8 pb-16">
        <div className="bg-white rounded-xl border border-border max-w-lg w-full overflow-hidden">

          {/* ── Step progress indicator (2-step flow only) ─────────────────── */}
          {totalSteps === 2 && (
            <div className="flex items-center justify-center gap-0 px-8 pt-7 pb-2">
              <div className="flex flex-col items-center gap-1.5">
                <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-bold font-mono transition-all ${step > 1 ? "bg-[#0025D8] border-[#0025D8] text-white" : "bg-[#0025D8] border-[#0025D8] text-white"}`}>
                  {step > 1 ? <Check className="w-3.5 h-3.5" /> : "1"}
                </div>
                <span className={`font-mono text-[9px] tracking-[0.12em] ${step === 1 ? "text-[#0025D8] font-bold" : "text-muted-foreground"}`}>APPLICATION</span>
              </div>
              <div className={`w-20 h-0.5 mb-5 mx-1 transition-colors ${step > 1 ? "bg-[#0025D8]" : "bg-border"}`} />
              <div className="flex flex-col items-center gap-1.5">
                <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-bold font-mono transition-all ${step === 2 ? "bg-[#0025D8] border-[#0025D8] text-white" : "border-border text-muted-foreground bg-white"}`}>
                  2
                </div>
                <span className={`font-mono text-[9px] tracking-[0.12em] ${step === 2 ? "text-[#0025D8] font-bold" : "text-muted-foreground"}`}>CONSENT</span>
              </div>
            </div>
          )}

          {/* ── STEP 1: Application ────────────────────────────────────────── */}
          {step === 1 && (
            <>
              <div className="px-8 pt-7 pb-5 border-b border-border">
                <div className="font-mono text-[10px] tracking-[0.25em] text-muted-foreground mb-0.5">
                  STEP 1 OF {totalSteps}
                </div>
                <h2 className="font-display font-bold text-lg tracking-tight">Your application</h2>
              </div>

              <form onSubmit={totalSteps === 2 ? handleStep1Next : handleSubmit} className="px-8 py-7 space-y-6">

                {/* Resume upload */}
                <div>
                  <label className="flex items-center gap-1 font-mono text-[10px] tracking-[0.15em] text-muted-foreground mb-2">
                    RESUME / CV <span className="text-[#E11D48] ml-0.5">*</span>
                  </label>
                  <div
                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false); }}
                    onDrop={handleDrop}
                    onClick={() => !submitting && !file && fileRef.current?.click()}
                    className={`rounded-xl border-2 border-dashed transition-all duration-150 ${
                      dragging
                        ? "border-[#0025D8] bg-[#0025D8]/5 scale-[1.01]"
                        : file
                        ? "border-[#10B981]/60 bg-[#10B981]/3 cursor-default"
                        : "border-border hover:border-[#0025D8]/60 hover:bg-[#0025D8]/3 cursor-pointer"
                    } ${submitting ? "pointer-events-none opacity-50" : ""}`}
                  >
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".pdf,.doc,.docx"
                      className="hidden"
                      onChange={(e) => handleFile(e.target.files[0])}
                    />

                    {file ? (
                      <div className="px-5 py-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-[#10B981]/10 flex items-center justify-center flex-shrink-0">
                          <FileText className="w-5 h-5 text-[#10B981]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-[#10B981] truncate">{file.name}</p>
                          <p className="font-mono text-[10px] text-muted-foreground mt-0.5">{formatBytes(file.size)}</p>
                          {parsing && (
                            <div className="flex items-center gap-1.5 mt-1">
                              <Loader2 className="w-2.5 h-2.5 animate-spin text-[#0025D8]" />
                              <span className="font-mono text-[9px] text-[#0025D8]">Extracting your details…</span>
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={clearFile}
                          className="flex-shrink-0 w-7 h-7 flex items-center justify-center text-muted-foreground hover:text-[#E11D48] hover:bg-[#E11D48]/5 transition-colors"
                          title="Remove file"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="py-10 text-center">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 transition-colors ${dragging ? "bg-[#0025D8]" : "bg-secondary"}`}>
                          <Upload className={`w-5 h-5 transition-colors ${dragging ? "text-white" : "text-muted-foreground"}`} />
                        </div>
                        <p className="text-sm font-semibold mb-1">
                          {dragging ? "Drop to upload" : "Drop your resume here"}
                        </p>
                        <p className="font-mono text-[10px] text-muted-foreground">or click to browse</p>
                        <p className="font-mono text-[9px] text-muted-foreground/60 mt-1">PDF, DOC, DOCX · max 10 MB</p>
                      </div>
                    )}
                  </div>
                </div>

                {file && (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-border" />
                    <span className="font-mono text-[9px] text-muted-foreground tracking-[0.1em]">
                      {parsing ? "FILLING IN YOUR DETAILS…" : "REVIEW YOUR DETAILS"}
                    </span>
                    <div className="flex-1 h-px bg-border" />
                  </div>
                )}

                <FormInput id="name" label="FULL NAME" required icon={User}>
                  <input
                    id="name"
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    disabled={submitting}
                    placeholder={parsing ? "Detecting from resume…" : "Jane Smith"}
                    className="w-full rounded-xl border border-border pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#0025D8] focus:ring-1 focus:ring-[#0025D8]/20 transition-all disabled:opacity-50 disabled:bg-secondary/30"
                  />
                </FormInput>

                <FormInput id="email" label="EMAIL ADDRESS" required icon={Mail}>
                  <input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    disabled={submitting}
                    placeholder={parsing ? "Detecting from resume…" : "jane@example.com"}
                    className="w-full rounded-xl border border-border pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#0025D8] focus:ring-1 focus:ring-[#0025D8]/20 transition-all disabled:opacity-50 disabled:bg-secondary/30"
                  />
                </FormInput>

                <FormInput id="linkedin" label="LINKEDIN URL" icon={LinkIcon} hint="Optional — detected from resume if available">
                  <input
                    id="linkedin"
                    type="text"
                    value={form.linkedin_url}
                    onChange={(e) => setForm((f) => ({ ...f, linkedin_url: e.target.value }))}
                    disabled={submitting}
                    placeholder={parsing ? "Detecting from resume…" : "https://linkedin.com/in/janesmith"}
                    className="w-full rounded-xl border border-border pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#0025D8] focus:ring-1 focus:ring-[#0025D8]/20 transition-all disabled:opacity-50 disabled:bg-secondary/30"
                  />
                </FormInput>

                {error && (
                  <div className="flex items-start gap-2.5 p-3.5 rounded-xl bg-[#E11D48]/5 border border-[#E11D48]/30">
                    <AlertCircle className="w-4 h-4 text-[#E11D48] flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-[#E11D48]">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting || parsing}
                  className="w-full rounded-xl bg-[#0025D8] hover:bg-[#001CB3] active:bg-[#0020A8] text-white font-bold py-3.5 text-sm tracking-wide transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {parsing ? "Parsing resume…" : totalSteps === 2 ? "Next: Consent Form →" : "Submit Application →"}
                </button>

                {totalSteps === 1 && (
                  <p className="font-mono text-[9px] text-muted-foreground text-center">
                    By submitting you agree your details will be shared with the hiring team.
                  </p>
                )}
              </form>
            </>
          )}

          {/* ── STEP 2: Consent Form ───────────────────────────────────────── */}
          {step === 2 && consentInfo?.consent_enabled && (
            <>
              <div className="px-8 pt-7 pb-5 border-b border-border">
                <div className="font-mono text-[10px] tracking-[0.25em] text-muted-foreground mb-0.5">STEP 2 OF 2</div>
                <h2 className="font-display font-bold text-lg tracking-tight flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-[#0025D8]" />
                  Consent & Eligibility
                </h2>
              </div>

              <form onSubmit={handleSubmit} className="px-8 py-7 space-y-6">

                {/* Consent template body */}
                <div className="rounded-xl border border-border bg-[#FAFAFA] p-4">
                  <div className="font-mono text-[9px] tracking-[0.2em] text-muted-foreground mb-3">CONSENT AGREEMENT</div>
                  <p className="text-[13px] text-foreground whitespace-pre-wrap leading-relaxed">
                    {renderedConsent}
                  </p>
                </div>

                {/* Work Authorization */}
                {consentInfo.require_work_authorization && (
                  <div>
                    <label className="flex items-center gap-1 font-mono text-[10px] tracking-[0.15em] text-muted-foreground mb-1.5">
                      WORK AUTHORIZATION <span className="text-[#E11D48] ml-0.5">*</span>
                    </label>
                    <select
                      value={workAuth}
                      onChange={(e) => setWorkAuth(e.target.value)}
                      disabled={submitting}
                      className="w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-[#0025D8] focus:ring-1 focus:ring-[#0025D8]/20 transition-all bg-white disabled:opacity-50"
                    >
                      <option value="">Select your work authorization status…</option>
                      {consentInfo.work_auth_options?.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Notice Period */}
                {consentInfo.require_notice_period && (
                  <div>
                    <label className="flex items-center gap-1 font-mono text-[10px] tracking-[0.15em] text-muted-foreground mb-1.5">
                      NOTICE PERIOD (DAYS)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="365"
                      value={noticePeriod}
                      onChange={(e) => setNoticePeriod(e.target.value)}
                      disabled={submitting}
                      placeholder="e.g. 14"
                      className="w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-[#0025D8] focus:ring-1 focus:ring-[#0025D8]/20 transition-all disabled:opacity-50"
                    />
                    <p className="font-mono text-[9px] text-muted-foreground mt-1">How many days notice you are required to give your current employer</p>
                  </div>
                )}

                {/* Earliest Start Date */}
                {consentInfo.require_earliest_start && (
                  <div>
                    <label className="flex items-center gap-1 font-mono text-[10px] tracking-[0.15em] text-muted-foreground mb-1.5">
                      EARLIEST START DATE
                    </label>
                    <input
                      type="date"
                      value={earliestStart}
                      min={new Date().toISOString().slice(0, 10)}
                      onChange={(e) => setEarliestStart(e.target.value)}
                      disabled={submitting}
                      className="w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-[#0025D8] focus:ring-1 focus:ring-[#0025D8]/20 transition-all disabled:opacity-50"
                    />
                  </div>
                )}

                {/* Agree checkbox */}
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={consentChecked}
                    onChange={(e) => setConsentChecked(e.target.checked)}
                    disabled={submitting}
                    className="mt-0.5 w-4 h-4 accent-[#0025D8] flex-shrink-0"
                  />
                  <span className="text-sm text-foreground leading-snug">
                    I have read the above consent agreement and agree to its terms. I confirm all information I've provided is accurate.
                  </span>
                </label>

                {error && (
                  <div className="flex items-start gap-2.5 p-3.5 rounded-xl bg-[#E11D48]/5 border border-[#E11D48]/30">
                    <AlertCircle className="w-4 h-4 text-[#E11D48] flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-[#E11D48]">{error}</p>
                  </div>
                )}

                {/* Submit progress */}
                {submitting && stepIdx >= 0 && (
                  <div className="space-y-2.5 p-4 rounded-xl bg-[#0025D8]/3 border border-[#0025D8]/20">
                    {SUBMIT_STEPS.map((label, i) => (
                      <div key={label} className={`flex items-center gap-2.5 font-mono text-[11px] transition-opacity ${i > stepIdx ? "opacity-25" : ""}`}>
                        {i < stepIdx ? (
                          <CheckCircle className="w-3.5 h-3.5 text-[#10B981] flex-shrink-0" />
                        ) : i === stepIdx ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-[#0025D8] flex-shrink-0" />
                        ) : (
                          <div className="w-3.5 h-3.5 rounded-full rounded-xl border border-border flex-shrink-0" />
                        )}
                        <span className={i === stepIdx ? "text-[#0025D8] font-semibold" : "text-muted-foreground"}>{label}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => { setStep(1); setError(null); setConsentChecked(false); setWorkAuth(""); setNoticePeriod(""); setEarliestStart(""); }}
                    disabled={submitting}
                    className="flex items-center gap-1.5 px-4 py-3.5 rounded-xl border border-border text-sm font-mono text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors disabled:opacity-50"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={submitting || !consentChecked}
                    className="flex-1 rounded-xl bg-[#0025D8] hover:bg-[#001CB3] active:bg-[#0020A8] text-white font-bold py-3.5 text-sm tracking-wide transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Submitting…" : "Submit Application →"}
                  </button>
                </div>
              </form>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
